/**
 * Live policy updates on the Online Policy list (2026-10-22) - the decisions, kept pure so they are tested directly.
 *
 * Messages are gathered for BATCH_MS, then:
 *   - a few policies that are on the page now -> each row is read again and patched in place (nothing else moves);
 *   - many at once (a user created a batch), a socket that came back (resync), or a policy that is not on the page
 *     while the first page is open (it may be new) -> ONE silent reload of the page, at most once every RELOAD_GAP_MS;
 *   - a policy whose DATA was edited, or that was deleted (kind "edited" / "deleted"), while it is on the page or the
 *     first page is open -> ONE silent reload (the row's name, vehicle, premium ... changed, not just its status);
 *   - a policy not on the page while a later page is open -> nothing (it cannot appear there by itself).
 */
export const BATCH_MS = 800;
export const RELOAD_GAP_MS = 5000;
export const PATCH_MAX = 3;

const rowId = (row) => String((row && (row._id || row.policyId || row.id)) || "");

/**
 * @param {{ids: string[], resync: boolean, reloadIds?: string[]}} batch  reloadIds = edited / deleted policies
 * @param {Array} rows   what the table shows now
 * @param {number} page  0-indexed page
 * @returns {{reload: boolean, patch: string[]}}
 */
export function planListUpdate(batch, rows, page) {
  const onPage = new Set((rows || []).map(rowId));
  const ids = Array.from(new Set((batch.ids || []).map(String).filter(Boolean)));
  const visible = ids.filter((id) => onPage.has(id));
  const unseen = ids.filter((id) => !onPage.has(id));
  if (batch.resync) return { reload: true, patch: [] };
  const reloadIds = (batch.reloadIds || []).map(String);
  if (reloadIds.some((id) => onPage.has(id)) || (reloadIds.length && page === 0)) return { reload: true, patch: [] };
  if (visible.length > PATCH_MAX) return { reload: true, patch: [] };
  if (unseen.length && page === 0) return { reload: true, patch: [] };
  return { reload: false, patch: visible };
}

/** The row fields a fresh single-policy read can safely overwrite (the list and the single API flatten jobs alike). */
const ROW_FIELDS = [
  "jobStatus", "parts", "statusLabel", "jobShape", "attempts", "maxAttempts", "lastError", "hasRecording",
  "relianceStageView", "holdRequested", "status", "policystatus", "policyNumber", "proposalNumber", "paymentLink",
];

export function rowPatchFromPolicy(policy) {
  if (!policy) return null;
  const patch = {};
  for (const k of ROW_FIELDS) if (policy[k] !== undefined) patch[k] = policy[k];
  return patch;
}
